import React, { useCallback, useEffect, useState } from "react";
import { api, formatError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { WidgetError } from "@/components/WidgetError";
import { fmtDate } from "@/lib/format";
import { toast } from "sonner";
import { RefreshCw, Play, CheckCircle2, AlertCircle, Clock, Lock } from "lucide-react";

const JOB_LABELS = {
  generate_due_invoices: "Auto-invoice",
  expire_trials: "Trial expiry",
  move_to_grace: "Move to grace",
  suspend_after_grace: "Suspend after grace",
  reconcile_payfast_events: "Reconcile PayFast",
  create_reminder_rows: "Reminder rows",
};

const STATUS_META = {
  ok:      { color: "text-green-700 bg-green-50 border-green-300", icon: <CheckCircle2 size={12}/>, label: "OK" },
  partial: { color: "text-amber-700 bg-amber-50 border-amber-300", icon: <AlertCircle size={12}/>, label: "PARTIAL" },
  failed:  { color: "text-red-700 bg-red-50 border-red-300",     icon: <AlertCircle size={12}/>, label: "FAILED" },
  locked:  { color: "text-slate-700 bg-slate-100 border-slate-300", icon: <Lock size={12}/>, label: "LOCKED" },
  running: { color: "text-blue-700 bg-blue-50 border-blue-300",  icon: <Clock size={12}/>, label: "RUNNING" },
};

export default function JobStatusModule() {
  const [status, setStatus] = useState(null);
  const [error, setError] = useState(null);
  const [running, setRunning] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);

  const load = useCallback(() => {
    setError(null);
    api.get("/platform/saas/jobs/status")
       .then(({ data }) => setStatus(data))
       .catch((e) => setError(e));
  }, []);

  useEffect(load, [load, reloadKey]);
  // Auto-refresh every 30 seconds so status stays fresh without polling storm
  useEffect(() => {
    const t = setInterval(() => setReloadKey((k) => k + 1), 30_000);
    return () => clearInterval(t);
  }, []);

  const runNow = async (name) => {
    if (!window.confirm(
      `Run job "${JOB_LABELS[name] || name}" now?\n\n` +
      "This uses the same lock as scheduled runs. Duplicate work is safe."
    )) return;
    setRunning(name);
    try {
      const { data } = await api.post(`/platform/saas/jobs/${name}/run`);
      toast.success(
        `${JOB_LABELS[name]} ${data.status} — ` +
        `${data.processed_count} processed, ${data.skipped_count} skipped` +
        (data.failed_count ? `, ${data.failed_count} failed` : "")
      );
      setReloadKey((k) => k + 1);
    } catch (e) {
      toast.error(formatError(e, "Job failed"));
    } finally { setRunning(null); }
  };

  if (error) {
    return <div className="max-w-5xl">
      <WidgetError error={error} onRetry={() => setReloadKey((k) => k + 1)}
                   title="Could not load job status" testId="jobs-status-error" />
    </div>;
  }

  return (
    <div className="max-w-5xl" data-testid="platform-jobs-module">
      <div className="flex items-center gap-3 mb-2">
        <h1 className="text-2xl sm:text-3xl font-black tracking-tight">Job Status</h1>
        <Button variant="outline" size="sm" onClick={() => setReloadKey((k) => k + 1)}
                data-testid="jobs-refresh"
                className="rounded flex items-center gap-2">
          <RefreshCw size={12}/> Refresh
        </Button>
        {status && (
          <span className={"label-overline px-2 py-0.5 rounded border " +
            (status.scheduler_running
              ? "bg-green-50 border-green-300 text-green-700"
              : "bg-red-50 border-red-300 text-red-700")}
                data-testid="jobs-scheduler-state">
            Scheduler: {status.scheduler_running ? "running" : "stopped"}
          </span>
        )}
      </div>
      <p className="text-sm text-slate-600 mb-6">
        Automated SaaS billing jobs. Scheduled daily 02:00–03:00 SAST; PayFast
        reconciliation every 15 minutes. Manual runs use the same MongoDB lock
        and are idempotent.
      </p>

      {!status ? (
        <div className="label-overline text-slate-500">Loading…</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {status.jobs.map((j) => (
            <JobCard key={j.job_name} job={j}
                     running={running === j.job_name}
                     onRun={() => runNow(j.job_name)} />
          ))}
        </div>
      )}
    </div>
  );
}

function JobCard({ job, running, onRun }) {
  const last = job.last_run;
  const meta = last ? (STATUS_META[last.status] || STATUS_META.ok) : null;
  return (
    <div className="bg-white border border-slate-200 rounded p-4"
         data-testid={`job-card-${job.job_name}`}>
      <div className="flex items-center justify-between mb-2">
        <div>
          <div className="label-overline">{job.job_name}</div>
          <div className="font-black text-lg tracking-tight">
            {JOB_LABELS[job.job_name] || job.job_name}
          </div>
        </div>
        <Button size="sm" onClick={onRun} disabled={running}
                data-testid={`job-run-${job.job_name}`}
                className="rounded bg-slate-900 hover:bg-slate-800 h-9">
          <Play size={12} className="mr-1.5"/>
          {running ? "Running…" : "Run now"}
        </Button>
      </div>
      <div className="grid grid-cols-3 gap-2 text-xs mt-3">
        <div>
          <div className="label-overline text-[10px]">Last run</div>
          <div className="font-mono text-[11px]">
            {last?.started_at ? fmtDate(last.started_at) : "—"}
          </div>
        </div>
        <div>
          <div className="label-overline text-[10px]">Status</div>
          {last ? (
            <span className={"inline-flex items-center gap-1 px-1.5 py-0.5 " +
                             "rounded border text-[10px] font-bold " + meta.color}
                  data-testid={`job-last-status-${job.job_name}`}>
              {meta.icon}{meta.label}
            </span>
          ) : <span className="text-slate-400">never</span>}
        </div>
        <div>
          <div className="label-overline text-[10px]">Next run</div>
          <div className="font-mono text-[11px]">
            {job.next_run ? fmtDate(job.next_run) : "—"}
          </div>
        </div>
      </div>
      {last && (
        <div className="mt-3 pt-3 border-t border-slate-100 grid grid-cols-3 gap-2 text-center">
          <Stat label="Processed" value={last.processed_count} tone="green" />
          <Stat label="Skipped" value={last.skipped_count} tone="slate" />
          <Stat label="Failed" value={last.failed_count} tone="red" />
        </div>
      )}
      {last?.error && (
        <div className="mt-2 text-[11px] text-red-700 font-mono break-all"
             data-testid={`job-last-error-${job.job_name}`}>
          {last.error}
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, tone }) {
  const tones = {
    green: "text-green-700",
    slate: "text-slate-600",
    red: value ? "text-red-700" : "text-slate-400",
  };
  return (
    <div>
      <div className="label-overline text-[10px]">{label}</div>
      <div className={"font-mono font-bold text-sm " + (tones[tone] || "")}>
        {value ?? 0}
      </div>
    </div>
  );
}
